import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Journey, Transition } from '@/api/types';
import { Callout } from '@/components/Callout';
import { FlowDiagram } from '@/components/FlowDiagram';
import { Select, Segmented } from '@/components/Select';
import { RangeBar } from '@/components/RangeBar';
import { StatTile } from '@/components/StatTile';
import { formatCompact } from '@/components/TrendChart';
import { useOverview } from '@/hooks/useOverview';
import { AnalyticsLoadError } from '@/components/AnalyticsLoadError';

/**
 * What visitors actually DO — the ordered pages of a visit.
 *
 * <b>This is the one analysis neither provider can give.</b> GA4's Data API reports page totals but
 * not order (path exploration lives in its own UI or a BigQuery export), and Clarity's export has
 * no journey dimension at all. The first-party tracker already sees every page view with a session
 * id, so the sequence costs nothing extra — it just has to be kept, which is what
 * <c>rum_seen_sessions.journey</c> does.
 *
 * <b>Single-page visits are excluded by default.</b> A one-page journey is a bounce, and including
 * them would fill the whole list with "/" and bury every real sequence.
 */
export function JourneysPage({ siteKey }: { siteKey: string }) {
  const { days, setDays, from, to, setRange, isCustomRange, data: overview } = useOverview(siteKey);
  const [country, setCountry] = useState('');
  const [hostname, setHostname] = useState('');
  const [minSteps, setMinSteps] = useState(2);
  const [flowView, setFlowView] = useState<'chart' | 'table'>('chart');

  const query = useQuery({
    queryKey: ['journeys', siteKey, from, to, country, hostname, minSteps],
    queryFn: () => api.journeys(siteKey, {
      from, to, minSteps, limit: 25,
      country: country || undefined,
      hostname: hostname || undefined,
    }),
    retry: false,
    placeholderData: (previous) => previous,
  });

  const data = query.data;
  const journeys = data?.journeys ?? [];
  const scoped = country || hostname;

  return (
    <>
      <RangeBar
        days={days} setDays={setDays} from={from} to={to} data={overview}
        setRange={setRange} isCustomRange={isCustomRange}
      />

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Scope</h2>
          {(country || hostname || minSteps !== 2) && (
            <button
              className="btn btn-ghost"
              onClick={() => { setCountry(''); setHostname(''); setMinSteps(2); }}
            >
              Reset scope
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="filter-grid">
            <Select
              label="Country"
              value={country}
              onChange={setCountry}
              placeholder="All countries"
              options={(data?.countries ?? []).map((c) => ({ value: c, label: c }))}
              hint="From the CDN edge header, not the page"
            />
            <Select
              label="Domain"
              value={hostname}
              onChange={setHostname}
              placeholder="All domains"
              options={(data?.hostnames ?? []).map((h) => ({ value: h, label: h }))}
              hint="Compare the three sites separately"
            />
            <Segmented
              label="Visit depth"
              value={minSteps}
              onChange={setMinSteps}
              options={[
                { value: 1, label: 'All visits' },
                { value: 2, label: '2+ pages' },
                { value: 3, label: '3+ pages' },
              ]}
            />
          </div>
        </div>
      </div>

      {query.isError && <AnalyticsLoadError error={query.error} what="journeys" />}

      {data && data.total_sessions === 0 && (
        <Callout tone="info" title="No journeys recorded yet">
          Journeys come from the first-party tracker, not from GA4 or Clarity — neither exposes page
          order through an API. They appear once <code>rum.js</code> is live on the site and a
          visitor views more than one page.
        </Callout>
      )}

      <div className="tile-grid">
        <StatTile
          label="Sessions with a journey"
          value={data ? formatCompact(data.total_sessions) : '—'}
          sub={scoped ? 'before scoping' : 'in range'}
        />
        <StatTile
          label="Distinct journeys"
          value={journeys.length > 0 ? String(journeys.length) : '—'}
          sub={`showing top ${journeys.length}`}
        />
        <StatTile
          label="Most common path"
          value={journeys[0] ? `${Math.round(journeys[0].share * 100)}%` : '—'}
          sub={journeys[0]?.journey ?? 'no journeys yet'}
        />
        <StatTile
          label="Avg pages per visit"
          value={journeys.length > 0 ? avgSteps(journeys).toFixed(1) : '—'}
          sub="across the shown journeys"
        />
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>
            Most-walked paths{country && ` — ${country}`}{hostname && ` — ${hostname}`}
          </h2>
          <span className="t-label subtle">share is of all journeys in scope, so the tail stays visible</span>
        </div>
        <div className="card-body">
          {journeys.length === 0 ? (
            <div className="empty-state t-body">
              {query.isError && !data ? 'Not available — see the error above.' : 'No journeys match this scope.'}
            </div>
          ) : (
            <ol className="journey-list">
              {journeys.map((j) => (
                <li key={j.journey} className="journey-row">
                  <div className="journey-steps">
                    {j.steps.map((step, i) => (
                      <span key={`${j.journey}-${i}`} className="journey-step">
                        {i > 0 && <span className="journey-arrow" aria-hidden>→</span>}
                        <code>{step}</code>
                      </span>
                    ))}
                  </div>
                  <div className="journey-meta t-label">
                    <span className="tabular">{formatCompact(j.sessions)} sessions</span>
                    <span className="subtle tabular">{(j.share * 100).toFixed(1)}%</span>
                  </div>
                  {/* A bar, not just a number: ranking by eye is what a list of percentages
                      makes hard, and this is fundamentally a magnitude comparison. */}
                  <div className="journey-bar" aria-hidden>
                    <span style={{ width: `${Math.max(1, j.share * 100)}%` }} />
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {(data?.transitions.length ?? 0) > 0 && (
        <>
          <div className="card">
            <div className="card-header">
              <h2 className="t-heading" style={{ margin: 0 }}>Flow</h2>
              <Segmented
                label=""
                value={flowView}
                onChange={setFlowView}
                options={[{ value: 'chart', label: 'Diagram' }, { value: 'table', label: 'Table' }]}
                inline
              />
            </div>
            <div className="card-body">
              {/* Both views ship. The diagram shows shape; the table gives exact numbers and is the
                  one a screen reader can actually read. */}
              {flowView === 'chart'
                ? <FlowDiagram transitions={data!.transitions} />
                : <TransitionTable transitions={data!.transitions} />}
            </div>
          </div>
        </>
      )}
    </>
  );
}

/**
 * Page-to-page hops.
 *
 * Separate from the journey list because it answers a different question: the list says which whole
 * routes are common, this says where people go NEXT from a given page — including routes too rare
 * to appear whole. Position 1 is the first hop of a visit, which is where a landing page either
 * works or does not.
 */
function TransitionTable({ transitions }: { transitions: Transition[] }) {
  const first = transitions.filter((t) => t.position === 1);
  const later = transitions.filter((t) => t.position > 1);

  // Rendered inside the Flow card, so no wrapper of its own.
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table">
        <thead>
          <tr>
            <th>From</th>
            <th>To</th>
            <th>Hop</th>
            <th className="num">Sessions</th>
          </tr>
        </thead>
        <tbody>
          {[...first, ...later].slice(0, 25).map((t) => (
            <tr key={`${t.from}->${t.to}@${t.position}`}>
              <td><code>{t.from}</code></td>
              <td><code>{t.to}</code></td>
              <td>{t.position === 1 ? 'first' : `#${t.position}`}</td>
              <td className="num">{formatCompact(t.sessions)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Session-weighted, not a plain mean of the rows — a rare 8-page path must not outweigh a common 2-page one. */
function avgSteps(journeys: Journey[]): number {
  const sessions = journeys.reduce((sum, j) => sum + j.sessions, 0);
  if (sessions === 0) return 0;
  return journeys.reduce((sum, j) => sum + j.step_count * j.sessions, 0) / sessions;
}
